import {
    fetchInfo,
    createOrder
} from "@/api/modules/products/fixedpricetickets";
import { makeUtcDate } from "@/utilities/time";

const InfoModel = function(info) {
    this.identifier = info.productTypeIdentifier;
    this.maximumNumberOfPassengersPerItem =
        info.maximumNumberOfPassengersPerItem;
    this.allowedPassengerTypeIds = info.allowedPassengerTypeIds;
    this.products = info.buyableProductVariants.map(
        product => new ProductModel(product, this.identifier)
    );
};

const ProductModel = function(productData, identifier) {
    this.id = productData.id;
    this.type = identifier;
    this.restrictToDate = productData.restrictToDate;
    this.imageAvailable = productData.imageAvailable;
    this.passengerTypePrices = productData.passengerTypePrices.map(pType => ({
        passengerTypeId: pType.passengerTypeId,
        price: pType.passengerTypePriceOre
    }));

    this.texts = {};

    Object.keys(productData.texts).forEach(language => {
        this.texts[language] = new ProductLanguageModel(
            language,
            productData.texts[language].name,
            productData.texts[language].descriptionPlainText,
            productData.texts[language].validityText
        );
    });

    this.validDurationMinutes = productData.validDurationMinutes;
    this.validWindowStartInstance = productData.validWindowStartInstant;
    this.validWindowEndInstance = productData.validWindowEndInstant;
};

const ProductLanguageModel = function(
    language,
    name,
    descriptionPlainText,
    validityText
) {
    this.language = language;
    this.name = name;
    this.description = descriptionPlainText;
    this.validityText = validityText;
};

export const state = {
    info: null
};

export const mutations = {
    updateInfo: (state, payload) => {
        state.info = new InfoModel(payload.info);
    }
};

export const getters = {};

export const actions = {
    fetchInfo: context => {
        // If we already have the list. we should not call the endpoint again
        if (context.state.info) {
            return Promise.resolve();
        }

        return fetchInfo(true)
            .then(response => {
                if (!response.ok) throw new Error(response.responseBody);
                return response.responseBody;
            })
            .then(response => {
                if (!response.buyableProductVariants) throw new Error(response);
                context.commit("updateInfo", { info: response });
            });
    },
    prepareOrder: (context, { product }) => {
        if (!product)
            throw new Error(
                "cannot prepare fixed price ticket order without a product"
            );
        const {
            allowedPassengerTypeIds,
            maximumNumberOfPassengersPerItem
        } = context.state.info;
        const serverTime = makeUtcDate(
            context.rootGetters.getServerDateAsMoment
        );

        // we should determine if the product was valid to buy in the past
        // we do this to know if we should push the selectable dates forward to today
        const wasProductValidToBuyInThePast = makeUtcDate(
            product.validWindowStartInstance
        ).isSameOrBefore(makeUtcDate(serverTime));

        const periodStart = wasProductValidToBuyInThePast
            ? makeUtcDate(serverTime).startOf("day")
            : makeUtcDate(product.validWindowStartInstance);
        const periodEnd =
            product.validWindowEndInstance === null
                ? null
                : makeUtcDate(product.validWindowEndInstance);

        context.commit("order/setDetails", { product }, { root: true });
        context.commit(
            "passenger/updatePassengers",
            {
                passengerTypeIds: allowedPassengerTypeIds,
                maximumPassengers: maximumNumberOfPassengersPerItem
            },
            { root: true }
        );
        context.dispatch(
            "date/prepareSelectableDates",
            { periodStart, periodEnd },
            { root: true }
        );
    },
    createOrder: context => {
        const passengers = context.rootGetters["passenger/getPassengers"].map(
            passenger => {
                return {
                    passengerTypeId: passenger.passengerType.id,
                    passengerTypeCount: passenger.count
                };
            }
        );
        const product = context.rootGetters["order/getDetails"].product;
        const validFromInstance =
            context.rootGetters["date/getDepartureDateAsMoment"];

        if (!product) throw new Error("cannot create order without a product");

        return createOrder(passengers, product.id, validFromInstance)
            .then(response => {
                if (!response.ok) throw new Error(JSON.stringify(response));
                return response.responseBody;
            })
            .then(({ orderId, priceOre, ...details }) => {
                context.commit("order/setOrderId", orderId, { root: true });
                context.commit("order/setPriceOre", priceOre, { root: true });
                context.commit(
                    "order/setDetails",
                    {
                        ...details,
                        product,
                        validFrom: makeUtcDate(details.validFromInstant),
                        validTo: makeUtcDate(details.validToInstant)
                    },
                    { root: true }
                );
            });
    }
};
